import { useAuthenticationContext } from '@/Contexts/App/AuthenticationContext';
import { isSuccesLogin, type FailLoginResult } from '@/Models/LoginResult';
import { DobAlert, DobButton, DobInput, DobSelect } from '@dedalusdiit/dob-components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as API from '../../API';
import type LoginData from '../../Models/LoginData';
import Styles from './Login.module.scss';

export interface LoginProps {
    redirectToAfterLogin?: string;
}

function getCookie(name: string): string | undefined {
    const nameLenPlus = (name.length + 1);
    return document.cookie
        .split(';')
        .map((cookie) => cookie.trim())
        .find((cookie) => cookie.substring(0, nameLenPlus) === `${name}=`)
        ?.split('=')[1];
}

const Login = (props: LoginProps): React.ReactElement => {
    const { t } = useTranslation();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [site, setSite] = useState(getCookie('ROWSite') ?? '');
    const [printer, setPrinter] = useState('');
    const [scanner, setScanner] = useState('');
    const [error, setError] = useState<string | null>();
    const [loginEnabled, setLoginEnabled] = useState<boolean>(false);
    const authenticationContext = useAuthenticationContext();
    const navigate = useNavigate();

    const { redirectToAfterLogin } = props;

    // Options
    const [useADAuth, setUseADAuth] = useState(false);
    const [useAdfsAuth, setUseAdfsAuth] = useState(false);
    const [hasMultipleSites, setHasMultipleSites] = useState(false);
    const [chooseEquipment, setChooseEquipment] = useState(false);
    const [siteOptions, setSiteOptions] = useState<any>([]);
    const [printerOptions, setPrinterOptions] = useState([]);
    const [scannerOptions, setScannerOptions] = useState([]);

    const tryLogin = (): void => {
        const loginData: LoginData = {
            Username: username,
            Password: password,
            LoginSite: site,
            PreferredScanner: '0',
            PreferredPrinter: '0',
        };
        if (chooseEquipment) {
            loginData.PreferredScanner = scanner;
            loginData.PreferredPrinter = printer;
        }

        setError(null);
        void authenticationContext?.login(loginData)
            .then((res) => {
                if (isSuccesLogin(res)) {
                    return navigate(redirectToAfterLogin ?? '/dashboard');
                }

                const loginFailMessage = generateLoginFailMessage(res);
                setError(loginFailMessage);
            });
    };

    const tryLoginAD = (): void => {
        setLoginEnabled(false);
        window.location.href = `/WinAuth/Login?loggedOnSite=${site}`;
    };

    const tryLoginAdfs = (): void => {
        setLoginEnabled(false);
        window.location.href = `/WinAuth/Login/Adfs?loggedOnSite=${site}`;
    };

    const generateLoginFailMessage = (failLoginResult: FailLoginResult): string => {
        const { code, statusText } = failLoginResult;
        if (code === 401) {
            return t('InvalidUsernameOrPassword');
        }

        return statusText ?? t('SomethingWentWrong');
    };

    const checkForWCF = async (): Promise<boolean> => {
        let found = false;
        await API.general.WCFData()
            .then((_) => {
                found = true; 
            })
            .catch((e) => {
                console.error('Check WCF Data error:', e); found = false; 
            });
        return found;
    };

    const getEquipment = (): void => {
        if (!chooseEquipment) {
            return;
        }
        void API.general.getEquipmentForSite(site)
            .then((equipment) => {
                setScanner(equipment.scanners[0].ID);
                setPrinter(equipment.printers[0].ID);
                setScannerOptions(equipment.scanners);
                setPrinterOptions(equipment.printers);
                setLoginEnabled(true);
            });
    };

    useEffect(() => {
        void API.general.getLoginOptions()
            .then((res) => {
                setUseADAuth(res.useADAuth);
                setUseAdfsAuth(res.useAdfsAuth);
                setHasMultipleSites(res.hasMultipleSites);
                setSiteOptions(res.sites);
                if (site === '') {
                    setSite(res.sites[0].Value);
                }
                void checkForWCF()
                    .then((found) => {
                        if (found || !res.chooseEquipment) {
                            setLoginEnabled(true);
                            return;
                        }
                        setChooseEquipment(res.chooseEquipment);
                    });
            });
    }, []);

    useEffect(getEquipment, [site, chooseEquipment]);

    const onEnter = (e: React.KeyboardEvent<HTMLDivElement>): void => {
        if (e.key !== 'Enter') {
            return;
        }
        tryLogin();
    };

    return (
        <div>
            <div id={Styles.logo}>
                <img alt="RISonWEB" src="/Content/img/RISLogo3_w.png" />
            </div>
            <div className={Styles.content}>
                <div className={Styles.loginContainer}>
                    <div className={Styles.header}>{t('Login')}</div>

                    <DobInput isRequired direction="vertical" label={t('UserName')} value={username} onValueChange={(v) => setUsername(v)} onKeyUp={onEnter} />

                    {/* autoComplete="new-password" disabled autocomplete for Chrome etc. Because the spec isn't implemented properly this seems weird */}
                    <DobInput autoComplete="new-password" isRequired direction="vertical" label={t('Password')} type="password" value={password} onValueChange={(v) => setPassword(v)} onKeyUp={onEnter} />

                    { hasMultipleSites
                        && (
                            <div>
                                <DobSelect
                                    direction="vertical"
                                    label={t('Site')}
                                    optionSelected={siteOptions.map((x: { Value: string; Text: string }) => ({ value: x.Value, label: x.Text })).find((x: { value: string }) => x.value === site)}
                                    onChange={(e) => {
                                        if (e) {
                                            setSite(e.value);
                                        }
                                    }}
                                    options={siteOptions.map((x: { Value: string; Text: string }) => ({ value: x.Value, label: x.Text }))}
                                />
                            </div>
                        )}
                    { chooseEquipment
                        && (
                            <>
                                <DobSelect
                                    direction="vertical"
                                    label={t('PreferredPrinter')}
                                    optionSelected={printerOptions.map((x: { Value: string; Name: string }) => ({ value: x.Value, label: x.Name })).find((x: { value: string }) => x.value === printer)}
                                    onChange={(e) => {
                                        if (e) {
                                            setPrinter(e.value);
                                        }
                                    }}
                                    options={printerOptions.map((x: { ID: string; Name: string }) => ({ value: x.ID, label: x.Name }))}
                                />

                                <DobSelect
                                    direction="vertical"
                                    label={t('PreferredScanner')}
                                    optionSelected={scannerOptions.map((x: { Value: string; Name: string }) => ({ value: x.Value, label: x.Name })).find((x: { value: string }) => x.value === scanner)}
                                    onChange={(e) => {
                                        if (e) {
                                            setScanner(e.value);
                                        }
                                    }}
                                    options={scannerOptions.map((x: { ID: string; Name: string }) => ({ value: x.ID, label: x.Name }))}
                                />
                            </>
                        )}

                    <div className={Styles.buttons}>
                        { useAdfsAuth
                            && <DobButton disabled={!loginEnabled} onClick={tryLoginAdfs}>{t('LoginADFS')}</DobButton>}
                        { useADAuth && !useAdfsAuth
                            && <DobButton disabled={!loginEnabled} onClick={tryLoginAD}>{t('LoginAD')}</DobButton>}
                        <DobButton onClick={tryLogin} disabled={!loginEnabled}>{t('Login')}</DobButton>
                    </div>

                    { error && <DobAlert type="error">{error}</DobAlert> }
                </div>
            </div>
        </div>
    );
};

export default Login;
